import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import OnboardingPanel from "@/components/OnboardingPanel";
import {
  TrendingUp, Wallet, FileText, CreditCard,
  Calendar, ArrowUpRight, Receipt, Banknote, Target, BookOpen, Settings, Sparkles,
  Landmark
} from "lucide-react";

const fmtEUR = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(
    Number.isFinite(Number(n)) ? Number(n) : 0
  );

// Formatage précis pour les métriques critiques (taux horaire) où les centimes comptent
const fmtEURPrecise = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    Number.isFinite(Number(n)) ? Number(n) : 0
  );

const MONTHS_FR = ["Janv.", "Févr.", "Mars", "Avr.", "Mai", "Juin", "Juil.", "Août", "Sept.", "Oct.", "Nov.", "Déc."];

export default function Home() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="flex justify-center items-center h-96 text-secondary-text"><div className="spinner mr-2" /> Chargement...</div>;

  const { kpi, chart, company } = data;
  const projectedNetPositive = kpi.projected_net >= 0;
  const netAfterTaxPositive = (kpi.projected_net_after_tax ?? 0) >= 0;
  const treasuryHealth = kpi.treasury_projected >= 0 ? "Saine" : "Tension";

  return (
    <div data-testid="home-dashboard" className="max-w-7xl mx-auto">
      <OnboardingPanel />
      {/* Header */}
      <header className="mb-12 animate-in">
        <div className="flex items-center gap-2 font-body text-xs font-semibold mb-4" style={{ color: "#FFC400" }}>
          <Sparkles size={12} strokeWidth={2} /> {MONTHS_FR[kpi.month - 1]} {kpi.year}
          <span className="ornament" style={{ background: "#FFC400", opacity: 0.4 }} />
          Cockpit
        </div>
        <h1 className="font-display text-[2.75rem] sm:text-[3.75rem] tracking-tight leading-[1.02]" style={{ fontWeight: 500 }}>
          Ton <span className="font-display" style={{ color: "#E10600" }}>360°</span> <span style={{ color: "var(--grey-soft)" }}>·</span> <span className="font-display" style={{ color: "#FFC400" }}>zéro angle mort</span>
        </h1>
        <p className="text-secondary-text mt-4 max-w-3xl text-[15px] leading-relaxed font-body">
          {company?.name && <>Vue d&apos;ensemble de <b>{company.name}</b> · </>}Chiffre d&apos;affaires, trésorerie et projection annuelle pour décider à temps de vos investissements.
        </p>
      </header>

      {/* KPI strip principal */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-6 animate-in delay-1">
        <Kpi
          label={`Devis signés ${company?.vat_franchise ? "(TTC)" : "(HT)"}`}
          value={fmtEUR(kpi.revenue_signed)}
          icon={FileText} tone="primary" testid="kpi-ca-signe"
        />
        <Kpi label="CA en cours" value={fmtEUR(kpi.revenue_in_progress)} sub="Devis envoyés à valider" icon={ArrowUpRight} tone="accent" testid="kpi-ca-encours" />
        <Kpi
          label="Trésorerie actuelle"
          value={fmtEUR(kpi.treasury_current)}
          sub="Solde réel à date"
          hint="Solde bancaire initial + encaissements reçus − dépenses payées"
          icon={Wallet}
          tone={kpi.treasury_current >= 0 ? "success" : "danger"}
          testid="kpi-treso-actu"
        />
        <Kpi
          label="Trésorerie prévisionnelle"
          value={fmtEUR(kpi.treasury_projected)}
          sub={`Vision fin ${kpi.year} · ${treasuryHealth}`}
          hint="Trésorerie actuelle + devis signés à encaisser − charges fixes à payer d'ici fin d'année"
          icon={TrendingUp}
          tone={kpi.treasury_projected >= 0 ? "success" : "danger"}
          testid="kpi-treso-prev"
        />
      </div>

      {/* Projection panel + Chart */}
      <div className="grid lg:grid-cols-3 gap-6 mb-6 animate-in delay-2">
        {/* Projection */}
        <div className="card lg:col-span-1" data-testid="projection-card">
          <div className="flex items-center gap-2 mb-1">
            <Target size={16} style={{ color: "#FFC400" }} strokeWidth={2} />
            <h2 className="font-display text-xl tracking-tight" style={{ fontWeight: 600 }}>Projection fin d&apos;année</h2>
          </div>
          <p className="font-body text-[11px] font-medium text-tertiary-text mb-5">Sur la base des moyennes mensuelles</p>
          <div className="space-y-3">
            <Row label="CA projeté" value={fmtEUR(kpi.projected_annual_revenue)} />
            <Row label="Charges projetées" value={fmtEUR(kpi.projected_annual_charges)} negative />

            <div className="border-t pt-4 mt-4" style={{ borderColor: "#3A3D42" }}>
              <div className="font-body text-[11px] font-medium text-tertiary-text font-semibold mb-1">
                Résultat net projeté <span className="opacity-60">(avant impôt)</span>
              </div>
              <div className="numerals leading-none" style={{
                color: projectedNetPositive ? "#FFC400" : "#E10600",
                fontSize: "clamp(1.3rem, 9cqi, 2.25rem)",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0,
              }} data-testid="projected-net">
                {projectedNetPositive ? "+" : ""}{fmtEUR(kpi.projected_net)}
              </div>
              <div className="text-[11px] text-tertiary-text mt-2 font-display font-body">Hors IS / IR — résultat fiscal brut.</div>
            </div>

            {/* IS / IR estimation */}
            <div className="rounded-lg px-4 py-3.5 mt-3 border" style={{ background: "#1F1F22", borderColor: "#3A3D42" }} data-testid="tax-card">
              <div className="flex items-center gap-2 mb-2">
                <Landmark size={13} style={{ color: "#E10600" }} strokeWidth={2} />
                <div className="font-body text-[11px] font-semibold" style={{ color: "#E10600" }}>{kpi.tax_label}</div>
              </div>
              <Row label="Impôt prévisionnel" value={fmtEUR(kpi.estimated_tax)} negative compact />
              <div className="border-t mt-3 pt-3" style={{ borderColor: "rgba(225, 6, 0, 0.18)" }}>
                <div className="font-body text-[9px]  text-tertiary-text font-semibold">Net après impôt</div>
                <div className="numerals leading-tight" style={{
                  color: netAfterTaxPositive ? "#F5F4F2" : "#E10600",
                  fontSize: "clamp(1.05rem, 7cqi, 1.65rem)",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0,
                }} data-testid="net-after-tax">
                  {netAfterTaxPositive ? "+" : ""}{fmtEUR(kpi.projected_net_after_tax)}
                </div>
              </div>
              <Link to="/setup" className="font-body text-[11px] font-medium hover:underline mt-2.5 inline-block" style={{ color: "#FFC400" }}>
                Régime fiscal : {kpi.tax_regime} →
              </Link>
            </div>

            <div className="text-xs text-secondary-text mt-3 font-body font-display">
              {projectedNetPositive
                ? "Vous pouvez envisager un investissement (véhicule, outillage, formation)."
                : "Prudence — limitez les investissements non essentiels."}
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="card lg:col-span-2" data-testid="chart-card">
          <div className="flex items-center justify-between mb-1">
            <div>
              <h2 className="font-display text-xl tracking-tight flex items-center gap-2" style={{ fontWeight: 600 }}>
                <Calendar size={16} style={{ color: "#FFC400" }} strokeWidth={2} /> Flux mensuel {kpi.year}
              </h2>
              <p className="font-body text-[11px] font-medium text-tertiary-text">Recettes vs dépenses · 12 mois (réel + prévisionnel)</p>
            </div>
          </div>
          <Chart data={chart} currentMonth={kpi.month} />
        </div>
      </div>

      {/* Secondary cards */}
      <div className="grid md:grid-cols-3 gap-6 mb-6 animate-in delay-3">
        <MiniCard icon={Banknote} label="Dépenses ce mois" value={fmtEUR(kpi.expenses_month)} sub={`${fmtEUR(kpi.expenses_year)} depuis janvier`} to="/treasury" cta="Saisir une dépense" />
        <MiniCard icon={Receipt} label="Charges fixes / mois" value={fmtEUR(kpi.fixed_monthly + kpi.employees_monthly)} sub={`${fmtEUR(kpi.fixed_monthly)} structure + ${fmtEUR(kpi.employees_monthly)} salaires`} to="/setup" cta="Modifier" />
        <MiniCard icon={Sparkles} label="Point mort taux horaire" value={`${fmtEURPrecise(data.hourly_break_even)}/h`} sub={`Marge cible ${data.target_margin_pct}%`} to="/setup" cta="Ajuster" />
      </div>

      {/* Quick access */}
      <div className="card animate-in delay-4">
        <h2 className="font-display text-xl flex items-center gap-2 mb-5 tracking-tight" style={{ fontWeight: 600 }}>
          <Sparkles size={16} style={{ color: "#FFC400" }} strokeWidth={2} /> Accès rapide
        </h2>
        <div className="grid md:grid-cols-3 gap-3 text-sm">
          {[
            { icon: BookOpen, title: "Référentiel ouvrages", desc: "Bibliothèque d'ouvrages, prix matières, temps de pose", to: "/library" },
            { icon: Settings, title: "Charges fixes mensuelles", desc: "Loyer, salariés, marge cible", to: "/setup" },
            { icon: CreditCard, title: "Devis & Factures", desc: "Builder, PDF, paiements partiels", to: "/quotes" },
          ].map(({ icon: Icon, title, desc, to }) => (
            <Link key={to} to={to} className="rounded-lg p-5 border hover:-translate-y-0.5 transition-all group" style={{ background: "#161618", borderColor: "#3A3D42" }}>
              <Icon size={18} style={{ color: "#FFC400" }} className="mb-2.5 group-hover:scale-110 transition-transform" strokeWidth={2} />
              <div className="font-display text-base tracking-tight" style={{ fontWeight: 600 }}>{title}</div>
              <div className="text-xs text-secondary-text mt-1 font-body">{desc}</div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, hint, icon: Icon, tone = "primary", testid }) {
  const toneStyle = {
    primary: { bg: "rgba(255, 196, 0, 0.08)", color: "#FFC400" },
    accent:  { bg: "rgba(255, 196, 0, 0.12)", color: "#FFC400" },
    success: { bg: "rgba(34, 197, 94, 0.14)", color: "#22C55E" },
    danger:  { bg: "rgba(225, 6, 0, 0.12)", color: "#E10600" },
  }[tone];
  const valueColor = tone === "success" ? "#22C55E" : tone === "danger" ? "#E10600" : undefined;
  return (
    <div className="kpi" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="kpi-label">{label}</div>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: toneStyle.bg, color: toneStyle.color }}>
          <Icon size={16} strokeWidth={2} />
        </div>
      </div>
      <div className="kpi-value" style={valueColor ? { color: valueColor } : undefined}>{value}</div>
      {sub && <div className="text-xs text-secondary-text font-body leading-snug">{sub}</div>}
      {hint && <div className="text-[10px] font-body italic leading-snug" style={{ color: "var(--grey)" }}>({hint})</div>}
    </div>
  );
}

function MiniCard({ icon: Icon, label, value, sub, to, cta }) {
  return (
    <div className="kpi">
      <div className="flex items-start justify-between gap-3">
        <div className="kpi-label">{label}</div>
        <Icon size={16} className="text-tertiary-text shrink-0" strokeWidth={1.6} />
      </div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="text-xs text-secondary-text font-body mt-1">{sub}</div>}
      {to && <Link to={to} className="inline-block mt-3 font-body text-[11px] font-medium hover:underline font-semibold" style={{ color: "#FFC400" }}>{cta} →</Link>}
    </div>
  );
}

function Row({ label, value, negative, compact }) {
  return (
    <div className={`flex items-center justify-between ${compact ? "text-xs" : "text-sm"}`}>
      <span className="text-secondary-text font-body">{label}</span>
      <span className={`font-mono tabular-nums`} style={{ color: negative ? "#E10600" : "#F5F4F2", fontWeight: 500 }}>{negative ? "− " : ""}{value}</span>
    </div>
  );
}

function Chart({ data, currentMonth }) {
  const maxAbs = Math.max(...data.map((d) => Math.max(Math.abs(d.revenue), Math.abs(d.expenses))), 1000);
  return (
    <div className="mt-5 space-y-2.5">
      {data.map((d) => {
        const isPast = d.month <= currentMonth;
        const wRev = (d.revenue / maxAbs) * 100;
        const wExp = (d.expenses / maxAbs) * 100;
        return (
          <div key={d.month} className="flex items-center gap-3">
            <div className={`w-16 flex items-center gap-1 font-mono text-xs ${isPast ? "" : "text-tertiary-text"}`} style={{ color: isPast ? "#F5F4F2" : undefined }}>
              {d.label}
              {!isPast && <span className="text-[10px] font-medium" style={{ color: "#FFC400" }}>prév.</span>}
            </div>
            <div className="flex-1 grid grid-cols-2 gap-2">
              <div className="flex items-center justify-end gap-2">
                <span className="font-mono text-[10px] tabular-nums text-tertiary-text">{fmtEUR(d.revenue)}</span>
                <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "rgba(255, 196, 0, 0.1)", width: `${wRev}%`, minWidth: d.revenue > 0 ? "2px" : "0" }}>
                  <div className="h-full rounded-full" style={{ background: isPast ? "#FFC400" : "rgba(255,196,0,0.45)", width: "100%" }}></div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "rgba(225, 6, 0, 0.1)", width: `${wExp}%`, minWidth: d.expenses > 0 ? "2px" : "0" }}>
                  <div className="h-full rounded-full" style={{ background: isPast ? "#E10600" : "rgba(225, 6, 0, 0.45)", width: "100%" }}></div>
                </div>
                <span className="font-mono text-[10px] tabular-nums text-tertiary-text">{fmtEUR(d.expenses)}</span>
              </div>
            </div>
          </div>
        );
      })}
      <div className="flex items-center gap-4 font-mono text-[10px] text-tertiary-text mt-4 pt-3 border-t flex-wrap" style={{ borderColor: "#3A3D42" }}>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: "#FFC400" }} /> Recettes</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: "#E10600" }} /> Dépenses & charges</span>
        <span className="flex items-center gap-1.5 ml-auto"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: "rgba(225, 6, 0, 0.45)" }} /> Prévisionnel</span>
      </div>
    </div>
  );
}
